// src/resources/sections/projectConfig.ts
import type { ResourceConfig } from "../resourceTypes";

export const PROJECT_CONFIG: ResourceConfig[] = [
  {
    name: "projects",
    label: "Projects",
    schema: "project",
    fields: [
      { source: "name", label: "name", kind: "text", list: true, form: true },
      {
        source: "description",
        label: "description",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "updated_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["name", "description"],
  },

  {
    name: "tasks",
    label: "Tasks",
    schema: "project",
    fields: [
      {
        source: "project_id",
        label: "project_id",
        kind: "uuid",
        form: true,
        list: false,
        ref: {
          resource: "projects",
          optionText: "name",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "parent_id",
        label: "parent_id",
        kind: "uuid",
        form: true,
        list: false,
        ref: {
          resource: "tasks",
          optionText: "name",
          input: "autocomplete",
          schema: "project",
        },
      },
      { source: "name", label: "name", kind: "text", list: true, form: true },
      {
        source: "description",
        label: "description",
        kind: "longtext",
        list: false,
        form: true,
      },
      {
        source: "status_id",
        label: "Status Type",
        kind: "number",
        list: true,
        form: true,
        ref: {
          resource: "task_status_types",
          optionText: "code",
          input: "select",
          schema: "project",
        },
      },
      {
        source: "priority_id",
        label: "Priority",
        kind: "number",
        list: true,
        form: true,
        ref: {
          resource: "task_priority_types",
          optionText: "code",
          input: "select",
          schema: "project",
        },
      },
      {
        source: "assignee_id",
        label: "assignee_id",
        kind: "uuid",
        form: true,
        list: false,
        ref: {
          resource: "users",
          optionText: "email",
          input: "autocomplete",
          schema: "iam",
        },
      },
      {
        source: "reporter_id",
        label: "reporter_id",
        kind: "uuid",
        form: true,
        list: false,
        editDisabled: true,
        ref: {
          resource: "users",
          optionText: "email",
          input: "autocomplete",
          schema: "iam",
        },
      },
      {
        source: "is_milestone",
        label: "is_milestone",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "start_at",
        label: "start_at",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "due_at",
        label: "due_at",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "started_at",
        label: "started_at",
        kind: "date",
        list: true,
        form: true,
        editDisabled: true,
      },
      {
        source: "completed_at",
        label: "completed_at",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "updated_at",
        label: "updated_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "estimated_hours",
        label: "estimated_hours",
        kind: "number",
        list: false,
        form: true,
      },
      {
        source: "actual_hours",
        label: "actual_hours",
        kind: "number",
        list: false,
        form: true,
      },
      {
        source: "computed_hours",
        label: "comptued_hours",
        kind: "number",
        list: false,
        form: true,
        editDisabled: true,
      },
    ],
    searchFields: [
      "name",
      "status_id",
      "priority_id",
      "assignee_id",
      "project_id",
    ],
  },

  {
    name: "task_comments",
    label: "Task Comments",
    schema: "project",
    fields: [
      {
        source: "task_id",
        label: "task_id",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "tasks",
          optionText: "title",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "body",
        label: "body",
        kind: "markdown",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "updated_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["task_id", "body"],
  },

  {
    name: "task_relations",
    label: "Task Relations",
    schema: "project",
    fields: [
      {
        source: "from_task",
        label: "from_task",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "tasks",
          optionText: "title",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "to_task",
        label: "to_task",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "tasks",
          optionText: "title",
          input: "autocomplete",
          schema: "project",
        },
      },
      { source: "kind", label: "kind", kind: "text", list: true, form: true },
    ],
    searchFields: ["from_task", "to_task", "kind"],
  },

  {
    name: "project_shares",
    label: "Project Shares",
    schema: "project",
    fields: [
      {
        source: "project_id",
        label: "project_id",
        kind: "uuid",
        form: true,
        list: true,
        editDisabled: true,
        ref: {
          resource: "projects",
          optionText: "name",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "group_id",
        label: "group_id",
        kind: "uuid",
        form: true,
        list: true,
        editDisabled: true,
        ref: {
          resource: "groups",
          optionText: "name",
          input: "autocomplete",
          schema: "iam",
        },
      },
      {
        source: "can_read",
        label: "can_read",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "can_write",
        label: "can_write",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "can_delete",
        label: "can_delete",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["project_id", "group_id"],
  },

  {
    name: "task_shares",
    label: "Task Shares",
    schema: "project",
    fields: [
      {
        source: "task_id",
        label: "task_id",
        kind: "uuid",
        form: true,
        list: true,
        editDisabled: true,
        ref: {
          resource: "tasks",
          optionText: "title",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "group_id",
        label: "group_id",
        kind: "uuid",
        form: true,
        list: true,
        editDisabled: true,
        ref: {
          resource: "groups",
          optionText: "name",
          input: "autocomplete",
          schema: "iam",
        },
      },
      {
        source: "can_read",
        label: "can_read",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "can_write",
        label: "can_write",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "can_delete",
        label: "can_delete",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["group_id", "task_id"],
  },

  {
    name: "task_status_history",
    label: "Task Status History",
    schema: "project",
    readOnly: true,
    defaultSort: { field: "changed_at", order: "desc" },
    fields: [
      {
        source: "task_id",
        label: "task_id",
        kind: "text",
        list: true,
        form: false,
        displayOnly: true,
        ref: {
          resource: "tasks",
          optionText: "name",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "from_status_id",
        label: "from_status_id",
        kind: "text",
        list: true,
        form: false,
        editDisabled: true,
        ref: {
          resource: "task_status_types",
          optionText: "label",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "to_status_id",
        label: "to_status_id",
        kind: "text",
        list: true,
        form: true,
        ref: {
          resource: "task_status_types",
          optionText: "label",
          input: "autocomplete",
          schema: "project",
        },
      },
      {
        source: "changed_by",
        label: "changed_by",
        kind: "uuid",
        list: true,
        form: false,
        editDisabled: true,
        ref: {
          resource: "users",
          optionText: "email",
          input: "autocomplete",
          schema: "iam",
        },
      },
      {
        source: "changed_at",
        label: "changed_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["task_id", "to_status_id"],
  },
];
